import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import AlertConfirmModal from '@components/Modal/AlertConfirmModal'

const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
  const [current, setCurrent] = useState(null)
  const lastOptionsRef = useRef(null)

  // 문자열은 message로 인식
  const normalizeOptions = (options, variant) => {
    // 문자열 → 자동 title/message 세팅
    if (typeof options === 'string') {
      return { 
        variant,
        title: variant === 'alert' ? '알림' : '확인',
        message: options 
      }
    }

    // 객체일 때도 title 없으면 자동 세팅
    if (!options?.title) {
      return {
        variant,
        title: variant === 'alert' ? '알림' : '확인',
        ...options
      }
    }

    // 객체이면 그대로 머지
    return { variant, ...(options || {}) }
  }

  // confirm()
  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      const normalized = normalizeOptions(options, 'confirm')
      lastOptionsRef.current = normalized
      setCurrent({ options: normalized, resolve })
    })
  }, [])

  // alert()
  const alert = useCallback((options) => {
    return new Promise((resolve) => {
      const normalized = normalizeOptions(options, 'alert')
      lastOptionsRef.current = normalized
      setCurrent({ options: normalized, resolve })
    })
  }, [])

  // confirm/alert 공통 close handler
  const closeCurrent = useCallback((result) => {
    if (!current) return
    const { resolve, options } = current

    const isAlert = options.variant === 'alert'
    const ok = isAlert ? result === 'ok' : result === 'confirm'

    resolve(ok)
    setCurrent(null)
  }, [current])

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert])

  // 현재 보여줄 옵션
  const opts = current?.options ?? lastOptionsRef.current ?? {}
  let {
    title = '',
    message = '',
    variant = 'confirm',
    confirmText = '확인',
    cancelText = '취소',
  } = opts

  // 서브텍스트(message)만 있고 title이 비어있으면 title로 승격
  let finalTitle = title
  let finalDescription = message

  if (!title && message) {
    finalTitle = message
    finalDescription = ''
  }

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      {/* AlertConfirmModal 대체 */}
      <AlertConfirmModal
        open={Boolean(current)}
        title={finalTitle}
        description={finalDescription}
        type={variant}
        confirmText={confirmText}
        cancelText={cancelText}
        onClose={() => closeCurrent('cancel')}
        hideCancel
        onConfirm={() => closeCurrent('confirm')}
      />
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>')
  return ctx
}
